import { AddInstallerForm } from 'components/Drawer/types';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useInstallers from 'hooks/useInstallers/useInstallers';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import AddInstaller from 'ui/organisms/Drawer/variants/AddInstaller';

const AddInstallerVariant: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { register, handleSubmit, setValue } = useForm<AddInstallerForm>();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { installersGroups, setInstallerMember, setInstallerMemberLoading } = useInstallers({
    handleFetchError
  });
  const { hideDrawer } = useDrawer();
  const [nameInputValue, setNameInputValue] = useState('');
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailInputValidation, setEmailInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);

  const onSubmit = async () => {
    setShowValidation(true);
    if (nameInputValidation === undefined && emailInputValidation === undefined && !setInstallerMemberLoading) {
      try {
        const response = await setInstallerMember(emailInputValue, nameInputValue);
        if (response?.setInstaller.name !== nameInputValue && !response?.setInstaller.isEditable)
          enqueueSnackbar(
            translation.the_user_you_have_added_into_atlas_is_already_existing_therefor_we_reuse_the_already_known_user_name,
            { snackbartype: 'success' }
          );
        hideDrawer();
      } catch {
        // useInstallers' onError already shows the message; keep the drawer open.
      }
    }
  };

  useEffect(() => {
    setValue('name', nameInputValue);
    setValue('email', emailInputValue);
  }, [emailInputValue, nameInputValue, setValue]);

  useEffect(() => {
    if (nameInputValue === '') setNameInputValidation(translation.name_cannot_be_empty);
    else setNameInputValidation(undefined);
  }, [nameInputValue, translation.name_cannot_be_empty]);

  useEffect(() => {
    if (emailInputValue === '') setEmailInputValidation(translation.email_required_error);
    else if (
      !emailInputValue
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    )
      setEmailInputValidation(translation.email_invalid_error);
    else if (installersGroups.some(({ members }) => members.some((member) => member.email === emailInputValue)))
      setEmailInputValidation(translation.email_duplicate_error);
    else setEmailInputValidation(undefined);
  }, [
    installersGroups,
    emailInputValue,
    translation.email_invalid_error,
    translation.email_duplicate_error,
    translation.email_required_error
  ]);

  return (
    <DrawerTemplate
      title={translation.add_installer}
      confirmButtonText={translation.add}
      id="drawer-add-installer-group"
      onSubmit={handleSubmit(onSubmit)}
      disableConfirmButton={showValidation && (nameInputValidation !== undefined || emailInputValidation !== undefined)}
    >
      <AddInstaller
        register={register}
        nameInputValue={nameInputValue}
        handleNameInputOnChange={setNameInputValue}
        nameInputValidation={nameInputValidation}
        emailInputValue={emailInputValue}
        handleEmailInputValueChange={setEmailInputValue}
        emailInputValidation={emailInputValidation}
        showValidation={showValidation}
      />
    </DrawerTemplate>
  );
};
export default AddInstallerVariant;
